import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faTowerBroadcast } from '@fortawesome/pro-light-svg-icons/faTowerBroadcast';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { HomeBlock } from '../HomeBlock';
import { HomeBlockSkeleton } from '../HomeBlockSkeleton';
import { HomeSetupCard } from '../HomeSetupCard';
import { HomePeriodDays } from '../homePeriod';
import { SocialChannelRows } from './SocialChannelRows';
import { SocialBestPost } from './SocialBestPost';
import { socialBlockMessages as messages } from './socialBlockMessages';
import { HomeSocialSummary } from './useHomeSocialSummary';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Social block; each part uses its own slice of it */
import styles from './SocialBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialBlockProps {
  /** The read of `useHomeSocialSummary`. The Home body owns it, because the Promote block reads one flag of it. */
  summary: HomeSocialSummary;
  days: HomePeriodDays;
}

/**
 * The Social block (design: music24-social-connectors d1, d2): one row per channel with its
 * followers and their change over the Home period, and the best post of the period. With no
 * channel it shows "Connect your channels". It loads and fails on its own, as every Home block.
 */
const SocialBlockComponent: React.FC<SocialBlockProps> = ({ summary, days }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openPerformance = useCallback(() => router.push(SMLRoutes.social), [router]);
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const openComposer = useCallback(() => router.push(SMLRoutes.promoteComposer), [router]);

  const title = intl.formatMessage(messages.title);

  if (summary.isLoading) {
    return (
      <HomeBlock icon={faTowerBroadcast} title={title}>
        <HomeBlockSkeleton />
      </HomeBlock>
    );
  }

  const hasChannel = summary.channels.length > 0;

  return (
    <HomeBlock
      icon={faTowerBroadcast}
      title={title}
      moreLabel={hasChannel ? intl.formatMessage(messages.open) : undefined}
      onMore={openPerformance}
      error={summary.error}
      retry={summary.retry}
    >
      {hasChannel ? (
        <div className={styles.grid}>
          <SocialChannelRows channels={summary.channels} days={days} onOpenChannels={openChannels} />
          <SocialBestPost post={summary.bestPost} days={days} onOpenComposer={openComposer} />
        </div>
      ) : (
        <HomeSetupCard
          icon={faTowerBroadcast}
          title={intl.formatMessage(messages.emptyTitle)}
          body={intl.formatMessage(messages.emptyBody)}
          cta={intl.formatMessage(messages.emptyCta)}
          onAction={openChannels}
          isBare
        />
      )}
    </HomeBlock>
  );
};

export const SocialBlock = TypedMemo(SocialBlockComponent);
